import { useState, useEffect } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { NumberInput, ActionIcon, Tooltip, Group, Text } from '@mantine/core';
import { IconTrash, IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import type { IConfiguracionComisiones } from '../interfaces';

export interface ComisionesTableMeta {
  actualizarFila: (id: string, campo: keyof IConfiguracionComisiones, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (fila: any) => void;
  cancelarEdicion?: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
}

/**
 * Comisión sugerida para un margen: 2,5% desde 20% de margen; por debajo cae 0,00769 puntos
 * por cada 0,1 de margen. Es solo una sugerencia: el usuario puede escribir otra comisión.
 */
export const calcularComisionSugerida = (margen: number) =>
  margen >= 20 ? 2.5 : Number((2.5 - ((20 - margen) / 0.1) * 0.00769).toFixed(5));

const EditableNumberCell = ({ getValue, row, column, table, suffix }: any) => {
  const initialValue = getValue() as number;
  const [value, setValue] = useState<number | string>(initialValue);
  const [touched, setTouched] = useState(false);
  const meta = table.options.meta as ComisionesTableMeta | undefined;
  const campo = column.id as keyof IConfiguracionComisiones;
  const rowId = row.original.Id;

  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    return <Text size="xs">{value}{suffix}</Text>;
  }

  return (
    <NumberInput
      size="xs"
      hideControls
      value={value}
      placeholder={campo === 'Comision' ? "Comisión *" : "Margen *"}
      required
      withAsterisk
      error={(touched || meta?.intentoGuardar) && (value === null || value === undefined || String(value).trim() === "")}
      onChange={(val) => {
        setTouched(true);
        setValue(val);
        if (meta) meta.actualizarFila(rowId, campo, val === "" ? "" : (Number(val) || 0));
      }}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
      suffix={suffix}
      decimalScale={5}
      allowNegative={false}
    />
  );
};

export const obtenerSemaforoYRango = (margen: number) => {
  if (margen >= 9.6) {
    return { color: 'green', rango: 'Activo' };
  } else if (margen <= 9.4) {
    return { color: 'red', rango: 'Afecta Comisión' };
  } else {
    return { color: 'yellow', rango: 'Base' };
  }
};

export const obtenerColumnasComisiones = (): ColumnDef<IConfiguracionComisiones>[] => [
  {
    id: "acciones",
    header: "Acciones",
    size: 90,
    cell: ({ row, table }) => {
      const meta = table.options.meta as ComisionesTableMeta | undefined;
      const isEditing = String(meta?.filaEnEdicion) === String(row.original.Id);
      const hayOtraFilaEnEdicion = meta?.filaEnEdicion !== null && meta?.filaEnEdicion !== undefined && String(meta?.filaEnEdicion) !== String(row.original.Id);
      
      if (isEditing) {
        return (
          <Group gap={4} justify="center">
            <Tooltip label="Guardar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => {
                const fila = row.original;
                const vacio = (v: unknown) => v === null || v === undefined || String(v) === "";
                if (vacio(fila.Margen) || vacio(fila.Comision)) {
                  meta?.mostrarNotificacionObligatorio?.();
                  return;
                }
                meta?.guardarFila?.(fila.Id);
              }}>
                <IconCheck size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Cancelar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" onClick={() => meta?.cancelarEdicion?.()}>
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      }
      return (
        <Group gap={4} justify="center">
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Editar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
             <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.iniciarEdicion?.(row.original)}>
               <IconEdit size={18} />
             </ActionIcon>
          </Tooltip>
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Eliminar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
            <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.eliminarFila(row.original.Id)}>
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    }
  },

  {
    id: "rango",
    header: "Rango",
    size: 150,
    cell: ({ row }) => {
      const margen = row.original.Margen || 0;
      const { color, rango } = obtenerSemaforoYRango(margen);
      const badgeColors: Record<string, string> = {
        'green': '#2b8a3e',
        'red': '#e03131',
        'yellow': '#f59f00'
      };

      return (
        <div style={{
          backgroundColor: badgeColors[color],
          color: 'white',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: 700,
          display: 'inline-block'
        }}>
          {rango}
        </div>
      );
    }
  },
  {
    accessorKey: "Margen",
    header: "Margen",
    cell: (props) => <EditableNumberCell {...props} suffix=" %" />,
    size: 150,
  },
  {
    accessorKey: "Comision",
    header: "Comisión",
    cell: (props) => <EditableNumberCell {...props} suffix=" %" />,
    size: 150,
  }
];
